import React, { useEffect, useState, useContext } from "react";
import {
  Button,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit } from "@material-ui/icons";
import AiPackageModal from "../../components/AiPackageModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import ForbiddenPage from "../../components/ForbiddenPage";
import AiModelsManager from "../../components/AiModelsManager";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const AiPackages = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [aiPackages, setAiPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  const [aiPackageModalOpen, setAiPackageModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingPackage, setDeletingPackage] = useState(null);

  useEffect(() => {
    const fetchPackages = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/ai-packages");
        setAiPackages(data);
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const handleOpenAiPackageModal = () => {
    setSelectedPackage(null);
    setAiPackageModalOpen(true);
  };

  const handleCloseAiPackageModal = () => {
    setSelectedPackage(null);
    setAiPackageModalOpen(false);
  };

  const handleEditAiPackage = (aiPackage) => {
    setSelectedPackage(aiPackage);
    setAiPackageModalOpen(true);
  };

  const handleDeleteAiPackage = async (aiPackageId) => {
    try {
      await api.delete(`/ai-packages/${aiPackageId}`);
      toast.success(i18n.t("aiPackages.deletePackageSuccess"));
      setAiPackages(aiPackages.filter((p) => p.id !== aiPackageId));
    } catch (err) {
      toastError(err);
    }
    setDeletingPackage(null);
  };

  const reloadPackages = async () => {
    const { data } = await api.get("/ai-packages");
    setAiPackages(data);
  };

  if (!user || !user.super) {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingPackage &&
          i18n.t("aiPackages.confirmDeletePackage", { name: deletingPackage.name })
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteAiPackage(deletingPackage.id)}
      >
        {i18n.t("aiPackages.deletePackageWarning")}
      </ConfirmationModal>
      <AiPackageModal
        open={aiPackageModalOpen}
        onClose={handleCloseAiPackageModal}
        aiPackageId={selectedPackage?.id}
        reloadPackages={reloadPackages}
      />
      <MainHeader>
        <Title>{i18n.t("aiPackages.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenAiPackageModal}
          >
            {i18n.t("aiPackages.addPackage")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">{i18n.t("aiPackages.colName")}</TableCell>
              <TableCell align="center">{i18n.t("aiPackages.colPrice")}</TableCell>
              <TableCell align="center">{i18n.t("aiPackages.colCredits")}</TableCell>
              <TableCell align="center">{i18n.t("aiPackages.colStatus")}</TableCell>
              <TableCell align="center">{i18n.t("aiPackages.colActions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {aiPackages.map((pkg) => (
                <TableRow key={pkg.id}>
                  <TableCell align="center">{pkg.name}</TableCell>
                  <TableCell align="center">${pkg.price}</TableCell>
                  <TableCell align="center">{pkg.credits}</TableCell>
                  <TableCell align="center">{pkg.status ? i18n.t("aiPackages.statusActive") : i18n.t("aiPackages.statusInactive")}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditAiPackage(pkg)}
                    >
                      <Edit />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeletingPackage(pkg);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutline />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={5} />}
            </>
          </TableBody>
        </Table>
      </Paper>
      
      <AiModelsManager />
      
    </MainContainer>
  );
};

export default AiPackages;
